import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { withStorageSync } from '@angular-architects/ngrx-toolkit';
import { initialStatus, LoadingStatus } from '@bcef/ia-shared';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { debounceTime, pipe, switchMap, tap } from 'rxjs';

import { ErrorStore } from '@shared/store/error.store';

import { PviRawForm } from '../interfaces/pvi-raw-form.interface';
import { PviRequest } from '../interfaces/pvi-request.interface';
import { PviResponse } from '../interfaces/pvi-response.interface';
import { PviService } from '../services/pvi.service';

export interface PviState {
  pviForm: PviRawForm;
  pviRequest: Partial<PviRequest>;
  pviResponse: PviResponse;
  loadingStatus: LoadingStatus;
}

export const initialState: PviState = {
  pviForm: {
    property: { propertyType: null, propertyUse: null, acquisitionOrigin: null },
    acquisition: {
      acquisitionDate: null,
      acquisitionPrice: null,
      acquisitionCosts: null,
      actualAcquisitionCosts: null,
    },
    sale: { saleDate: null, salePrice: null, saleFee: null },
    renovation: { hasRenovation: null, renovationAmount: null },
  },
  pviRequest: {},
  pviResponse: {
    saleProceeds: 0,
    netProceeds: 0,
    incomeTax: 0,
    socialContributions: 0,
    capitalGainSurtax: 0,
    totalTaxes: 0,
    adjustedCostBasis: 0,
    grossCapitalGain: 0,
    effectiveTaxRate: 0,
    renovationMessage: '',
    anniversaryMessage: '',
    suggestions: [],
    holdingPeriodYears: 0,
    taxableCapitalGain: 0,
    taxOutcomeState: '',
  },
  loadingStatus: initialStatus,
};
export const PVI_FORM_DEBOUNCE_MILLIS = 300;

export const PviStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withStorageSync({ key: 'ia-simulateur-epargne:pvi', storage: () => sessionStorage }),
  withMethods(
    (
      store,
      pviService = inject(PviService),
      errorStore = inject(ErrorStore),
      router = inject(Router),
    ) => ({
      sendPviForm: rxMethod<void>(
        pipe(
          tap(() =>
            patchState(store, (state) => ({
              loadingStatus: { ...state.loadingStatus, isLoading: true },
            })),
          ),
          debounceTime(PVI_FORM_DEBOUNCE_MILLIS),
          switchMap(() =>
            pviService.postPviSimulation(store.pviRequest()).pipe(
              tapResponse({
                next: (pviResponse) => {
                  patchState(store, (state) => ({
                    pviResponse,
                    loadingStatus: { ...state.loadingStatus, isLoaded: true },
                  }));
                  router.navigate(['pvi', 'summary']);
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setError(error);
                  patchState(store, (state) => ({
                    loadingStatus: { ...state.loadingStatus, error },
                  }));
                  router.navigate(['erreur']);
                },
                finalize: () =>
                  patchState(store, (state) => ({
                    loadingStatus: { ...state.loadingStatus, isLoading: false },
                  })),
              }),
            ),
          ),
        ),
      ),
      addRawFormData(formGroupData: PviRawForm): void {
        patchState(store, { pviForm: formGroupData });
      },
      addResponses(pviRequest: Partial<PviRequest>): void {
        patchState(store, { pviRequest });
      },
    }),
  ),
);
export type IPviStore = InstanceType<typeof PviStore>;
